import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/new/lib/utils';

const buttonVariants = cva(
  'inline-flex relative items-center select-none border justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500 ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-800 [&_.iconify]:text-gray-600',
        primary:
          'border-teal-400 bg-teal-50 text-teal-600 hover:border-teal-500 hover:bg-teal-100/80 hover:text-teal-600',
        destructive: 'border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:text-red-700',
        warn: 'border-amber-400 bg-amber-50 text-amber-600 hover:border-amber-500 hover:text-amber-700',
        outline: 'border border-input bg-background hover:bg-gray-100 hover:text-gray-800',
        ghost: 'bg-transparent border-none text-gray-600 hover:text-gray-900',
        link: 'text-primary underline-offset-4 hover:underline', // TODO
        blue: 'border-sky-400 bg-sky-50 text-sky-600 hover:border-sky-500 hover:bg-sky-100 hover:text-sky-600',
      },
      size: {
        default: 'px-2.5 py-1.5',
        sm: 'rounded-md px-2 py-1 text-xs gap-1.5',
        lg: 'rounded-md px-8',
        icon: 'p-2.5',
        iconSm: 'p-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
